/* eslint-disable no-magic-numbers */
import { useState, useEffect, useRef } from 'react';
import { FormEvent } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

import { Checkbox as MuiCheckbox } from '@mui/material';

import { PodcastSpotAPI } from 'api/podcastSpotAPI/index';
import { TestObjectiveAPI } from 'api/testObjectiveAPI';
import { Form, Formik, useFormikContext } from 'formik';

import { OutlinedButton, SubmitButton, Checkbox, DatePickerGroup, Label, Select } from 'components/UIComponents';
import { FieldGroupTitleContainer } from 'components/UIComponents/layout/styledFormComponents';
import { StyledDialogActions as DialogActions } from 'components/base/BaseModal/styledDialogActions';
import { StyledCheckboxContainer as CheckboxContainer } from 'components/base/BaseModal/styledDialogActions';
import { StyledDialogContent as DialogContent } from 'components/base/BaseModal/styledDialogContent';

import { usePopup } from 'context/GlobalPopupContext';
import { useToast } from 'context/ToastContext';

import { ReactComponent as CheckboxChecked } from 'assets/icons/CheckboxChecked.svg';
import { ReactComponent as CheckboxUnchecked } from 'assets/icons/CheckboxUnchecked.svg';

import { getResponseHandler } from 'helpers/forms';
import { podcastValidationSchema } from 'helpers/validation/podcastValidationSchema';

import { ModelName } from 'constants/enums/ModelName';
import { ToastType } from 'constants/enums/PopupType';
import { SpotType } from 'constants/enums/SpotTypes';
import { spotMessages } from 'constants/messages/spots';

import { IResponse } from 'interfaces/api';
import { IPodcastForm, IRawOriginSpot } from 'interfaces/spots/Podcast/IPodcastForm';
import { ITestObjectiveOption } from 'interfaces/TestObjective/ITestObjective';

import { colors } from 'styles/globalStyles';

import DependentSelectsBlock from '../commonComponents/DependentSelectsBlock';
import BonusTypeSection from './BonusTypeSection';
import { datesFieldStyles } from './styledComponents';

import { OrderAPI } from 'api/orderAPI';
import RESPONSE_CODES from '../../../../constants/responseCodes';
import { isDigitallyInsertedOptions } from '../../../../constants/outletOptions';
import PodcastRateFields from '../commonComponents/PodcastRateFields';

interface PodcastFormProps {
  onModalClose: () => void;
  successAction: () => void;
  editPodcastId: number;
  isPaid?: boolean;
}

export const pixelOptions = [
  { key: 'Required', value: 'Required' },
  { key: 'Preferred', value: 'Preferred' },
  { key: 'Not Required', value: 'Not Required' },
];

export const readOptions = [
  { key: 'Host Read', value: 'Host Read' },
  { key: 'Producer Read', value: 'Producer Read' },
  { key: 'Pre Produced', value: 'Pre Produced' },
];

export const endorsementOptions = [
  { key: 'PE Required', value: 'PE Required' },
  { key: 'PE Preferred', value: 'PE Preferred' },
  { key: 'N/A', value: 'N/A' },
];

export const positionOptions = [
  { key: 'Pre-Roll', value: 'Pre-Roll' },
  { key: 'Mid-Roll', value: 'Mid-Roll' },
  { key: 'Post-Roll', value: 'Post-Roll' },
  { key: 'Pre/Mid Rotator', value: 'Pre/Mid Rotator' },
  { key: 'Pre/Mid Combo', value: 'Pre/Mid Combo' },
];

const TestObjectivesSection = ({
  disabled,
  editPodcastId,
}: {
  disabled: boolean;
  editPodcastId?: number;
}) => {
  const { values, setFieldValue } = useFormikContext<IPodcastForm>();
  const [options, setOptions] = useState<{ key: string; value: number | '' }[]>([]);
  const skipNextAutoPopulateRef = useRef<boolean>(!!editPodcastId);

  useEffect(() => {
    let cancelled = false;

    if (!values.productId || !values.startDate) {
      setOptions([]);
      return () => { cancelled = true; };
    }

    const startDateIso = values.startDate;
    if (!/^\d{4}-\d{2}-\d{2}$/.test(startDateIso)) return () => { cancelled = true; };
    const year = startDateIso.slice(0, 4);

    TestObjectiveAPI.search(values.productId as number, year)
      .then((response: any) => {
        if (cancelled) return;
        const objectives = response?.data?.data || [];
        setOptions(
          objectives.map((obj: ITestObjectiveOption) => ({
            key: `${obj.number} - ${obj.brief_description}`,
            value: obj.id,
          }))
        );
      })
      .catch(() => {
        if (cancelled) return;
        setOptions([]);
      });

    if (skipNextAutoPopulateRef.current) {
      skipNextAutoPopulateRef.current = false;
      return () => { cancelled = true; };
    }

    if (!values.outletId) {
      setFieldValue('primaryTestObjectiveId', '');
      setFieldValue('secondaryTestObjectiveId', '');
      return () => { cancelled = true; };
    }

    PodcastSpotAPI.getRecentTestObjectives({
      outletId: values.outletId as number,
      productId: values.productId as number,
      year,
      startDate: startDateIso,
      excludeId: editPodcastId || undefined,
    })
      .then((response: any) => {
        if (cancelled) return;
        const data = response?.data?.data || {};
        setFieldValue('primaryTestObjectiveId', data.primary_test_objective_id ?? '');
        setFieldValue('secondaryTestObjectiveId', data.secondary_test_objective_id ?? '');
      })
      .catch(() => {
        if (cancelled) return;
        setFieldValue('primaryTestObjectiveId', '');
        setFieldValue('secondaryTestObjectiveId', '');
      });

    return () => { cancelled = true; };
  }, [values.outletId, values.productId, values.startDate, setFieldValue, editPodcastId]);

  return (
    <>
      <FieldGroupTitleContainer>TEST OBJECTIVES</FieldGroupTitleContainer>
      <Select
        name="primaryTestObjectiveId"
        label="Primary"
        options={options}
        disabled={disabled || !values.productId || !values.startDate}
        truncateOptions
      />
      <Select
        name="secondaryTestObjectiveId"
        label="Secondary"
        options={options}
        disabled={disabled || !values.productId || !values.startDate}
        truncateOptions
      />
    </>
  );
};

const PodcastForm = ({ onModalClose, successAction, editPodcastId, isPaid = false }: PodcastFormProps) => {
  const [isDisabled, setIsDisabled] = useState(false);
  const { openPopup, openSequentialPopups } = usePopup();
  const { onToastOpen } = useToast();
  const [podcastFormInformation, setPodcastFormInformation] = useState<IPodcastForm>({
    outletId: '',
    productId: '',
    creativeId: '',
    codeId: '',
    startDate: '',
    endDate: '',
    impressions: '',
    grossRate: '',
    netRate: '',
    cpmRate: '',
    isPixelRequired: 'Required',
    read: 'Host Read',
    endorsement: 'PE Required',
    position: 'Mid-Roll',
    isTestShow: false,
    includesVideoImpressions: false,
    isDigitallyInserted: 'Baked In',
    splitFromSpotId: '',
    primaryTestObjectiveId: '',
    secondaryTestObjectiveId: '',
    bonusType: '',
    originSpotIds: [],
    originSpots: [],
  });
  const [withOrder, setWithOrder] = useState(false);
  const [orderId, setOrderId] = useState(0);
  const [createAnother, setCreateAnother] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (editPodcastId) {
      PodcastSpotAPI.getOne(editPodcastId).then(({ data: { data } }: IResponse) => {
        setPodcastFormInformation({
          outletId: data.outlet_id,
          outletName: data.outlet_name || '',
          productId: data.product_id,
          productName: data.product_name || '',
          creativeId: data.creative_id || '',
          creativeName: data.creative_name || '',
          codeId: data.code_id || '',
          codeName: data.code || '',
          startDate: data.start_date || '',
          endDate: data.end_date || '',
          impressions: data.impressions,
          grossRate: data.gross_rate,
          netRate: data.net_rate,
          cpmRate: data.cpm_rate,
          isPixelRequired: data.is_pixel_required || 'Required',
          read: data.read || 'Host Read',
          endorsement: data.endorsement || 'PE Required',
          position: data.position || 'Mid-Roll',
          isTestShow: data.is_test_show,
          includesVideoImpressions: data.includes_video_impressions,
          isDigitallyInserted: data.is_digitally_inserted || 'Baked In',
          splitFromSpotId: data.split_from_spot_id,
          primaryTestObjectiveId: data.primary_test_objective_id || '',
          secondaryTestObjectiveId: data.secondary_test_objective_id || '',
          bonusType: data.bonus_type ?? '',
          originSpotIds: ((data.origin_spots ?? []) as IRawOriginSpot[]).map((s) => s.id),
          originSpots: ((data.origin_spots ?? []) as IRawOriginSpot[]).map((s) => ({
            id: s.id,
            outletId: s.outlet_id,
            outletName: s.outlet_name,
            productId: s.product_id,
            productName: s.product_name,
            startDate: s.start_date,
            totalGross: s.total_gross,
          })),
        });

        if (data.order_id) {
          setWithOrder(true);
          setOrderId(data.order_id);
        }
      });
    }
  }, [editPodcastId]);

  const reloadList = (spotId: string) => {
    const currentUrl = location.pathname;
    const searchParams = new URLSearchParams(location.search);

    const existingSpotIds = searchParams.get('spotId')?.split(',') || [];

    if (!existingSpotIds.includes(spotId)) {
      existingSpotIds.push(spotId);
    }

    searchParams.set('spotId', existingSpotIds.join(','));

    const newUrl = `${currentUrl}?${searchParams.toString()}`;
    successAction();
    navigate(newUrl);
  };

  const checkOrderChange = async (podcastId: number, updater: () => Promise<void>, startDate: string) => {
    const response = await OrderAPI.checkSpotOrderChange(SpotType.podcast,
      { spot_id: editPodcastId, start_date: startDate }, orderId);

    if (response.status === RESPONSE_CODES.UNPROCESSABLE_ENTITY) {
      openPopup(response.data.error);
    } else {
      const messages = response.data.data.messages;
      if (Array.isArray(messages) && messages.length > 0) {
        openSequentialPopups(popupDataCustomizer(messages), async () => {
          await updater();
          await orderChange(editPodcastId, startDate, true);
        });
      } else {
        await updater();
        await orderChange(editPodcastId, startDate, false);
      }
    }
  };

  const popupDataCustomizer = (popupData: any[]) => {
    return popupData.map(data => ({
      ...data,
      closeCallback: () => {
        setIsDisabled(false);
      },
    }));
  };

  const orderChange = (podcastId: number, startDate: string, changeOrder: boolean) => {
    const orderInfo = {
          outlet_id: podcastFormInformation.outletId,
          spots_ids: [podcastId],
          type: SpotType.podcast,
          start_date: startDate,
          change: changeOrder,
        };

    OrderAPI.orderChangeAfterSpotUpdate(orderInfo, orderId).then((response: IResponse) => {
      if (response.status !== RESPONSE_CODES.CREATED) {
        openPopup(response.data.error);
      }
    });
  };

  return (
    <>
      <Formik
        initialValues={podcastFormInformation}
        validationSchema={podcastValidationSchema}
        validateOnBlur
        enableReinitialize
        onSubmit={(values, actions) => {
          const responseHandler = getResponseHandler(
            ModelName.PodcastSpot,
            () => {
              if (!createAnother) {
                onModalClose();
              }
              if (!editPodcastId) {
                onToastOpen('SPOT_CREATED', 'Spot record has been successfully created', ToastType.success);
              }
              successAction();
            },
            actions
          );

          if (editPodcastId) {
            const updater = () =>
              PodcastSpotAPI.updatePodcast(editPodcastId, values)
                .then(responseHandler)
                .finally(() => setIsDisabled(false));
            if (withOrder) {
              openPopup({
                ...spotMessages['M-152'],
                submitCallback: () => checkOrderChange(editPodcastId, updater, values.startDate),
                closeCallback: () => setIsDisabled(false),
              });
            } else {
              updater();
            }
          } else {
            PodcastSpotAPI.createPodcast(values)
              .then((response: IResponse) => {
                const createdPodcastId = response.data.data.id;
                if (!createAnother) {
                  onModalClose();
                }
                if (!editPodcastId) {
                  onToastOpen('SPOT_CREATED', 'Spot record has been successfully created', ToastType.success);
                }
                reloadList(createdPodcastId);
              })
              .finally(() => setIsDisabled(false));
          }
        }}
      >
        {({
          errors,
          handleSubmit,
          dirty,
          values,
          setFieldValue,
          setFieldError,
          setFieldTouched,
          handleBlur,
          validateForm,
        }) => {
          return (
            <Form>
              <DialogContent>
                <FieldGroupTitleContainer>PODCAST INFO</FieldGroupTitleContainer>
                <DependentSelectsBlock
                  values={values}
                  setFieldValue={setFieldValue}
                  formInformation={podcastFormInformation}
                  spotType={SpotType.podcast}
                  isEdit={!!editPodcastId}
                  isPaid={isPaid}
                  isSplit={!!editPodcastId && !!podcastFormInformation.splitFromSpotId}
                />
                <FieldGroupTitleContainer>AIR PERIOD</FieldGroupTitleContainer>
                <DatePickerGroup
                  label="Air Dates"
                  startProps={{
                    name: 'startDate',
                    disabled: !!editPodcastId && !!podcastFormInformation.splitFromSpotId,
                    required: true,
                    onChange: () => {
                      if (values.endDate) {
                        setFieldTouched('endDate', true);
                      }
                    },
                  }}
                  endProps={{ name: 'endDate', minDate: values.startDate,
                    disabled: !!editPodcastId && !!podcastFormInformation.splitFromSpotId }}
                  styles={datesFieldStyles}
                />
                <FieldGroupTitleContainer>PAYMENT INFO</FieldGroupTitleContainer>
                <PodcastRateFields
                  impressionsValue={values.impressions}
                  grossRateValue={values.grossRate}
                  grossRateError={errors.grossRate}
                  netRateValue={values.netRate}
                  netRateError={errors.netRate}
                  cpmRateValue={values.cpmRate}
                  cpmRateError={errors.cpmRate}
                  setFieldValue={setFieldValue}
                  isPaid={isPaid}
                />
                <FieldGroupTitleContainer>ADDITIONAL INFO</FieldGroupTitleContainer>
                <Checkbox name="isTestShow" label="Test show"
                          disabled={!!editPodcastId && !!podcastFormInformation.splitFromSpotId} />
                <Checkbox name="includesVideoImpressions" label="Video Impressions"
                          disabled={!!editPodcastId && !!podcastFormInformation.splitFromSpotId} />
                <Select
                  name="isDigitallyInserted"
                  label="Digitaly Inserted"
                  options={isDigitallyInsertedOptions}
                  required
                  disabled={!!editPodcastId && !!podcastFormInformation.splitFromSpotId}
                />
                <Select
                  name="isPixelRequired"
                  label="Pixel"
                  options={pixelOptions}
                  required
                  disabled={!!editPodcastId && !!podcastFormInformation.splitFromSpotId}
                />
                <Select
                  name="read"
                  label="Read"
                  options={readOptions}
                  required
                  disabled={!!editPodcastId && !!podcastFormInformation.splitFromSpotId}
                />
                <Select
                  name="endorsement"
                  label="Endorsement"
                  options={endorsementOptions}
                  required
                  disabled={!!editPodcastId && !!podcastFormInformation.splitFromSpotId}
                />
                <Select
                  name="position"
                  label="Position"
                  options={positionOptions}
                  required
                  disabled={!!editPodcastId && !!podcastFormInformation.splitFromSpotId}
                />
                <BonusTypeSection
                  disabled={!!values.splitFromSpotId}
                  currentSpotId={editPodcastId || undefined}
                />
                <TestObjectivesSection
                  disabled={!!values.splitFromSpotId}
                  editPodcastId={editPodcastId}
                />
              </DialogContent>
              <DialogActions>
                {!editPodcastId && (
                  <CheckboxContainer>
                    <MuiCheckbox
                      checked={createAnother}
                      checkedIcon={<CheckboxChecked color={colors.red} />}
                      icon={<CheckboxUnchecked />}
                      onClick={() => setCreateAnother(!createAnother)}
                      style={{ padding: '8px 9px' }}
                    />
                    <Label>Create Another</Label>
                  </CheckboxContainer>
                )}
                <OutlinedButton onClick={onModalClose}>Cancel</OutlinedButton>
                <SubmitButton
                  disabled={!dirty || !!Object.values(errors).length || isDisabled}
                  onClick={(values: FormEvent<HTMLFormElement>) => {
                    setIsDisabled(true);
                    handleSubmit(values);
                  }}
                >
                  {editPodcastId ? 'Save' : 'Add'}
                </SubmitButton>
              </DialogActions>
            </Form>
          );
        }}
      </Formik>
    </>
  );
};

export default PodcastForm;
